import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertColor } from "@mui/material";
import classNames from "classnames";
import CustomizedSnackbars from "../../../components/common/customizedSnackbars";
import TooltipWrapper from "../../../components/common/TooltipWrapper";
import StatusPill from "../../cashouts/CashoutCard/StatusPill";
import { ROUTES } from "../../../setup/constants";
import { getDate } from "../../../setup/util/dateFormatter";
import {
  ICompletedTransferBody,
  isAutoCashOutServiceRequester,
} from "../../../setup/api/services/completed-transfers/models";
import {
  AUTO_CASH_OUT_SERVICE_LABEL,
  EMPTY_VALUE,
  REQUESTER_TYPE_LABELS,
  TABLE_HEADERS,
} from "../constants";
import styles from "./styles.module.scss";

interface ICompletedTransfersV2ListItem {
  transfer: ICompletedTransferBody;
}

const CompletedTransfersV2ListItem = ({
  transfer,
}: ICompletedTransfersV2ListItem) => {
  const { t } = useTranslation();

  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>("info");
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const openInNewTab = useCallback((path: string): void => {
    window.open(path, "_blank");
  }, []);

  const openUser = useCallback(
    (userId: string): void => openInNewTab(`${ROUTES.STAT_USER}${userId}`),
    [openInNewTab]
  );

  const openCashout = useCallback(
    (domainId: string): void =>
      openInNewTab(`${ROUTES.CASHOUTS}/${domainId}?isOperation=true`),
    [openInNewTab]
  );

  const openWithdraw = useCallback(
    (withdrawId: string): void =>
      openInNewTab(`${ROUTES.WITHDRAWS}/${withdrawId}?isOperation=true`),
    [openInNewTab]
  );

  const onCopy = useCallback(
    async (value?: string | number) => {
      if (!value) return;
      try {
        await navigator?.clipboard?.writeText(String(value));
        setSnackBarColor("success");
      } catch (error) {
        setSnackBarColor("error");
        console.error(error);
      }
      setSnackBarTitle(t("COPIED"));
      setSnackBarOpen(true);
    },
    [t]
  );

  const paymentMap = useMemo(() => {
    return {
      BANK: t("lbl_bank_transfer"),
      TABBY: t("lbl_tabby"),
      TAMARA: t("lbl_tamara"),
      NEO: `${t("lbl_neo_amount")} (neo later)`,
      MOYASAR: t("lbl_moyasar"),
      MANUAL: t("lbl_manual_transfer"),
      AUTO: t("lbl_auto_transfer"),
      REFUND: t("lbl_refund"),
      MULTI: t("lbl_multi_transfer"),
    };
  }, [t]);

  // The automated cashout service is not a real user: label, never a link.
  const isServiceRequester = isAutoCashOutServiceRequester(transfer);

  const requesterNameLabel = isServiceRequester
    ? AUTO_CASH_OUT_SERVICE_LABEL
    : transfer.requesterName || EMPTY_VALUE;

  /** Only a human requester resolves to a user page. */
  const openRequester =
    !isServiceRequester && transfer.requesterId
      ? () => openUser(transfer.requesterId as string)
      : undefined;

  const createRow = (desc: string, value) => {
    return { desc, value };
  };

  /** A cell whose value navigates elsewhere in the admin when clicked. */
  const linkCell = (label: string, onClick?: () => void) =>
    onClick ? (
      <div className={styles["transaction_link"]}>
        <p dir="rtl" onClick={onClick}>
          {label}
        </p>
      </div>
    ) : (
      <div className={styles["transaction_cell"]}>
        <p dir="rtl">{label}</p>
      </div>
    );

  /** A long identifier: truncated, copyable, full value in the tooltip. */
  const copyableCell = (tooltipId: string, value?: string | number) =>
    value ? (
      <div className={classNames(styles["transaction_payment"])}>
        <TooltipWrapper id={tooltipId} content={String(value)}>
          <img
            src="/assets/images/svg/copy.svg"
            alt=""
            onClick={() => onCopy(value)}
          />
          <p dir="rtl">{String(value)}</p>
        </TooltipWrapper>
      </div>
    ) : (
      <div className={styles["transaction_cell"]}>
        <p dir="rtl">{EMPTY_VALUE}</p>
      </div>
    );

  const rows = [
    createRow(
      TABLE_HEADERS.USERNAME,
      linkCell(
        transfer.beneficiaryName || transfer.beneficiaryId || EMPTY_VALUE,
        transfer.beneficiaryId
          ? () => openUser(transfer.beneficiaryId)
          : undefined
      )
    ),
    createRow(
      TABLE_HEADERS.BENEFICIARY_ID,
      linkCell(
        transfer.beneficiaryId || EMPTY_VALUE,
        transfer.beneficiaryId
          ? () => openUser(transfer.beneficiaryId)
          : undefined
      )
    ),
    createRow(
      TABLE_HEADERS.REQUESTER_NAME,
      linkCell(requesterNameLabel, openRequester)
    ),
    createRow(
      TABLE_HEADERS.REQUESTER_ID,
      linkCell(transfer.requesterId || EMPTY_VALUE, openRequester)
    ),
    createRow(
      TABLE_HEADERS.REQUESTER_TYPE,
      <div className={styles["transaction_cell"]}>
        <p dir="rtl">
          {transfer.requesterType
            ? REQUESTER_TYPE_LABELS[transfer.requesterType] ||
              transfer.requesterType
            : EMPTY_VALUE}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.STATUS,
      <div className={styles["status"]}>
        <StatusPill status={transfer.status} />
      </div>
    ),
    createRow(
      TABLE_HEADERS.DOMAIN_ID,
      linkCell(
        transfer.domainId || EMPTY_VALUE,
        transfer.domainId ? () => openCashout(transfer.domainId) : undefined
      )
    ),
    createRow(
      TABLE_HEADERS.TRANSACTION_ID,
      copyableCell(
        `transaction_id_tooltip_${transfer.id}`,
        transfer.transactionId
      )
    ),
    createRow(
      TABLE_HEADERS.GATEWAY_PAYMENT_ID,
      copyableCell(
        `gateway_payment_id_tooltip_${transfer.id}`,
        transfer.gatewayPaymentId
      )
    ),
    createRow(
      TABLE_HEADERS.REFERENCE_TRANSACTION_ID,
      copyableCell(
        `reference_transaction_id_tooltip_${transfer.id}`,
        transfer.referenceTransactionId
      )
    ),
    createRow(
      TABLE_HEADERS.AMOUNT,
      <div className={styles["transaction_bold"]}>
        <p dir="rtl">
          {typeof transfer.amount === "number"
            ? `${transfer.amount.toFixed(2)} ${t("lbl_saudi_currency")}`
            : EMPTY_VALUE}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSFER_CHANNEL,
      <div className={styles["transaction_cell"]}>
        <p dir="rtl">{paymentMap[transfer.doneThru] || EMPTY_VALUE}</p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSFER_TYPE,
      <div className={styles["transaction_cell"]}>
        <p dir="rtl">
          {paymentMap[transfer.paymentTransactionType] || EMPTY_VALUE}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.MANUAL_WITHDRAW,
      linkCell(
        transfer.manualWithdrawId
          ? String(transfer.manualWithdrawId)
          : EMPTY_VALUE,
        transfer.manualWithdrawId
          ? () => openWithdraw(String(transfer.manualWithdrawId))
          : undefined
      )
    ),
    createRow(
      TABLE_HEADERS.CREATED_AT,
      <div
        className={classNames(
          styles["transaction_cell"],
          styles["transaction_large_cell"]
        )}>
        <p dir="rtl">
          {transfer.createdAt
            ? getDate(new Date(transfer.createdAt))
            : EMPTY_VALUE}
        </p>
      </div>
    ),
    createRow(
      TABLE_HEADERS.TRANSFER_TIME,
      <div
        className={classNames(
          styles["transaction_cell"],
          styles["transaction_large_cell"]
        )}>
        <p dir="rtl">
          {transfer.updatedAt
            ? getDate(new Date(transfer.updatedAt))
            : EMPTY_VALUE}
        </p>
      </div>
    ),
  ];

  useEffect(() => {
    if (snackBarOpen) {
      setTimeout(() => {
        setSnackBarOpen(false);
        setSnackBarColor("info");
        setSnackBarTitle("");
      }, 5000);
    }
  }, [snackBarOpen]);

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  return (
    <>
      <tr className={styles.table_row}>
        {rows.map((row, index) => (
          <td className={styles.table_value} key={index}>
            {row.value}
          </td>
        ))}
      </tr>
      <div className={styles.snackbar}>{renderSnackBar()}</div>
    </>
  );
};

export default CompletedTransfersV2ListItem;
